import { isNaN } from "underscore";
/**
 * Round number using exponential notation strategy
 * @param num number to be rounded
 * @param precision number of decimal places (-1 for no rounding)
 * @returns rounded number
 */
export const round = (num: number, precision = 2): number => {
  if (precision < 0) {
    return num;
  }
  const value = Math.round(Number(num.toString() + "e" + precision.toString()));
  return Number(value + "e" + -precision);
};

export const customParseFloat = (value: any): any => {
  const result = parseFloat(value);
  if (isNaN(result)) {
    return 0;
  }
  return result;
};

/**
 * Amortization is the paying off of debt with a fixed repayment schedule in
 * regular installments over a period of time.
 *
 * @param principal - the amount being financed.
 * @param rate - the annual interest rate as a percentage (e.g. 6 for 6%).
 * @param period - the number of periods, expressed in years or months per `yearOrMonth`.
 * @param yearOrMonth - 0 when the period is entered in years, 1 when entered in months.
 * @param payAtBeginning - when truthy, payments are made at the start of each period, so the first period accrues no interest.
 * @returns the fixed monthly payment, rounded to two decimal places.
 */
export function AM(principal: number, rate: number, period: number, yearOrMonth: number, payAtBeginning?: number) {
  let numerator = 0;
  let denominator = 1;
  const ratePerPeriod = rate / 12 / 100;

  const buildNumerator = (numInterestAccruals: number): number => {
    if (payAtBeginning) {
      // If payments are made in the beginning of the period, then interest
      // shouldn't be calculated for first period
      numInterestAccruals -= 1;
    }

    return ratePerPeriod * Math.pow(1 + ratePerPeriod, numInterestAccruals);
  };

  if (!yearOrMonth) {
    // for inputs in years
    numerator = buildNumerator(period * 12);
    denominator = Math.pow(1 + ratePerPeriod, period * 12) - 1;
  } else if (yearOrMonth === 1) {
    // for inputs in months
    numerator = buildNumerator(period);
    denominator = Math.pow(1 + ratePerPeriod, period) - 1;
  }

  const am = principal * (numerator / denominator);
  return Math.round(am * 100) / 100;
}
